
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { PlusCircle, Edit, Archive, ArrowRightLeft } from "lucide-react";
import { EquipmentDialog } from "./EquipmentDialog";
import { Badge } from "@/components/ui/badge";

interface Equipment {
  id: string;
  name: string;
  base_price: number;
  custom_status?: string; // Using a custom status field instead
}

interface EquipmentInventoryProps {
  refreshTrigger: number;
  onDataChange: () => void;
}

export const EquipmentInventory = ({ refreshTrigger, onDataChange }: EquipmentInventoryProps) => {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    fetchEquipment();
  }, [refreshTrigger]);
  
  const fetchEquipment = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('equipment_types')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      // Transform the data to match the Equipment interface
      const equipmentItems: Equipment[] = (data || []).map(item => ({
        id: item.id,
        name: item.name,
        base_price: item.base_price || 0,
        // Add a default custom_status based on some criteria or just use 'active' as default
        custom_status: 'active'
      }));
      
      setEquipment(equipmentItems);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load equipment: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStatusChange = async (id: string, newStatus: 'active' | 'inactive' | 'archived') => {
    try {
      // Since there's no status field in the actual DB, we could use this function
      // to update some other field or implement custom behavior
      // For now, let's just update the local state
      setEquipment(prevEquipment => 
        prevEquipment.map(item => 
          item.id === id ? { ...item, custom_status: newStatus } : item
        )
      );
      
      toast({
        title: "Status updated",
        description: `Equipment status changed to ${newStatus}`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to update status: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  const openAddDialog = () => {
    setSelectedEquipment(null);
    setShowDialog(true);
  };
  
  const openEditDialog = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setShowDialog(true);
  };
  
  const handleDialogClose = (dataChanged: boolean) => {
    setShowDialog(false);
    if (dataChanged) {
      fetchEquipment();
      onDataChange();
    }
  };
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 text-white">Active</Badge>;
      case 'inactive':
        return <Badge variant="outline" className="bg-gray-200">Inactive</Badge>;
      case 'archived':
        return <Badge variant="destructive">Archived</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Equipment Inventory</h2>
        <Button onClick={openAddDialog}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Equipment
        </Button>
      </div>
      
      {isLoading ? (
        <div>Loading equipment...</div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Unit Cost</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {equipment.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No equipment items found. Add your first item to get started.
                  </TableCell>
                </TableRow>
              ) : (
                equipment.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>€{item.base_price.toFixed(2)}</TableCell>
                    <TableCell>{getStatusBadge(item.custom_status || 'active')}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        
                        {item.custom_status === 'active' ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleStatusChange(item.id, 'inactive')}
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                          </Button>
                        ) : item.custom_status === 'inactive' ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-green-500 border-green-500 hover:bg-green-50"
                            onClick={() => handleStatusChange(item.id, 'active')}
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                          </Button>
                        ) : null}
                        
                        {item.custom_status !== 'archived' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-500 border-red-500 hover:bg-red-50"
                            onClick={() => handleStatusChange(item.id, 'archived')}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
      
      {showDialog && (
        <EquipmentDialog
          equipment={selectedEquipment}
          open={showDialog}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
};
